import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  updateEmail,
  updateProfile,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import {
  getStorage,
  ref,
  getDownloadURL,
  uploadBytesResumable,
} from "firebase/storage";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import ProfileImage from "../Images/Avatar.png";
import { GoPencil } from "react-icons/go";
import { RxCross2 } from "react-icons/rx";
import {
  FiUser,
  FiMail,
  FiCalendar,
  FiGlobe,
  FiAtSign,
  FiPhone,
  FiBriefcase,
  FiLock,
} from "react-icons/fi";
import { db } from "../firebase/config";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

/* ---------- UI helpers (presentation only) ---------- */

const inputClass =
  "w-full bg-white/[0.03] hover:bg-white/[0.06] text-white text-[13px] md:text-[14px] " +
  "h-12 pl-11 pr-10 rounded-[14px] border border-[#ffffff1a] outline-none transition-all duration-200 " +
  "placeholder:text-white/35 focus:border-[#4268FF] focus:bg-white/[0.06] focus:ring-4 focus:ring-[#4268FF]/20";

function Field({ label, htmlFor, icon: Icon, hasError, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={htmlFor}
        className="text-white/70 font-normal text-[13px] text-start"
      >
        {label}
      </label>
      <div className="relative group">
        {Icon && (
          <Icon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-white/40 transition-colors duration-200 group-focus-within:text-[#4268FF]" />
        )}
        {children}
        {hasError && (
          <RxCross2 className="bg-[red] rounded-full p-[2px] h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-white" />
        )}
      </div>
    </div>
  );
}

const TABS = [
  { id: "personal", label: "Informations personnelles", short: "Personnel", icon: FiUser },
  { id: "pro", label: "Informations professionnelles", short: "Professionnel", icon: FiBriefcase },
];

export default function ProfileModify() {
  const auth = getAuth();
  const user = auth.currentUser;

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      name: user?.displayName,
      email: user?.email,
      date: "",
      residence: "France",
      pseudo: "",
      phoneNumber: "",
      fonction: "",
    },
  });

  const [currentError, setCurrentError] = useState("");
  const [countries, setCountries] = useState([]);

  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingEmailUpdate, setPendingEmailUpdate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState("personal");
  const activeIndex = TABS.findIndex((t) => t.id === activeTab);

  const reauthenticateAndUpdateEmail = async (newEmail, currentPassword) => {
    setIsSubmitting(true);
    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Now update email
      await updateEmail(user, newEmail);

      toast.success("Email mis à jour avec succès !", {
        position: "top-center",
      });

      setShowPasswordModal(false);
      setPendingEmailUpdate(null);

      // Continue with other profile updates using current form data
      const currentData = getValues();

      const profileUpdates = {
        displayName: currentData.name,
        phoneNumber: currentData.phoneNumber,
      };

      await updateProfile(user, profileUpdates);

      const userDocRef = doc(db, "users", user?.uid);
      await updateDoc(userDocRef, {
        date: currentData?.date,
        residence: currentData?.residence,
        pseudo: currentData?.pseudo,
        fonction: currentData?.fonction,
      });

      toast.success("Profil mis à jour avec succès !", {
        position: "top-center",
      });
    } catch (error) {
      console.error("Re-authentication error:", error);
      if (error.code === "auth/wrong-password") {
        setCurrentError("Mot de passe incorrect");
      } else {
        setCurrentError(error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadProfileImage = async (file) => {
    if (file) {
      const storage = getStorage();
      const metadata = {
        contentType: file.type,
        name: file.name,
        fileName: file.name,
        size: file.size,
        contentDisposition: `attachment; filename=${file.name}`,
      };
      const storageRef = ref(
        storage,
        `images/profile/${user.uid}/${file.name}`
      );

      console.log("Storeage Ref", storageRef);

      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      setIsLoading(true);
      uploadTask.on(
        "state_changed",

        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progress);
        },
        (error) => {
          console.error("Échec du téléchargement", error);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const docRef = doc(db, "users", user.uid);

          await setDoc(
            docRef,
            {
              photoURL: downloadURL,
            },
            { merge: true }
          );
          await updateProfile(user, { photoURL: downloadURL }).then(() => {
            toast.success("Image de profil téléchargée avec succès...", {
              position: "top-center",
            });
            setIsLoading(false);
          });
        }
      );
    }
    return null;
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Handle email update first
      if (data.email !== user.email) {
        try {
          await updateEmail(user, data.email);
          toast.success("Email mis à jour avec succès !", {
            position: "top-center",
          });
        } catch (emailError) {
          if (emailError.code === "auth/requires-recent-login") {
            // Show password modal for re-authentication
            setPendingEmailUpdate(data.email);
            setShowPasswordModal(true);
            return; // Exit early to wait for re-authentication
          } else if (emailError.code === "auth/operation-not-allowed") {
            // Use verification method
            await verifyBeforeUpdateEmail(user, data.email);
            toast.success(
              "Email de vérification envoyé à votre nouvelle adresse.",
              {
                position: "top-center",
              }
            );
            // Don't return here - continue with other profile updates
          } else {
            throw emailError;
          }
        }
      }

      // Continue with other profile updates
      const profileUpdates = {
        displayName: data.name,
        phoneNumber: data.phoneNumber,
      };

      console.log("Data", profileUpdates);
      await updateProfile(user, profileUpdates);

      const userDocRef = doc(db, "users", user?.uid);
      await updateDoc(userDocRef, {
        date: data?.date,
        residence: data?.residence,
        pseudo: data?.pseudo,
        fonction: data?.fonction,
      });

      // Only show this success message if email wasn't updated or if it was updated successfully
      if (data.email === user.email) {
        toast.success("Profil mis à jour avec succès !", {
          position: "top-center",
        });
      }
    } catch (error) {
      console.error("Update error:", error);

      // Handle specific error messages
      if (error.code === "auth/requires-recent-login") {
        setCurrentError("Veuillez vous reconnecter pour changer votre email");
      } else if (error.code === "auth/invalid-email") {
        setCurrentError("Format d'email invalide");
      } else if (error.code === "auth/email-already-in-use") {
        setCurrentError("Cette adresse email est déjà utilisée");
      } else {
        setCurrentError(error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(
          "https://restcountries.com/v3.1/all?fields=name"
        );
        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error("Unexpected country data format");
        }

        const countryNames = data
          .map((country) => country?.name?.common)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));

        setCountries(countryNames);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, []);

  const handleValidation = () => {
    const values = getValues();
    clearErrors();
    setCurrentError("");

    if (!values.email) {
      setError("email", { type: "required", message: "L'e-mail est requis" });
      setCurrentError("Email is required");
      return false;
    }
    setCurrentError("");
    return true;
  };

  return (
    <>
      {/* smooth panel transition (respects reduced motion) */}
      <style>{`
        @keyframes pm-panel-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pm-panel-in { animation: pm-panel-in .35s cubic-bezier(.22,1,.36,1) both; }
        @media (prefers-reduced-motion: reduce) { .pm-panel-in { animation: none; } }
      `}</style>

      <div className="mx-auto w-full max-w-3xl flex flex-col gap-6 py-[30px] px-0">
        {/* ---------- Page heading ---------- */}
        <div className="flex flex-col gap-1 text-center sm:text-start">
          <h2 className="text-white text-[20px] md:text-[26px] font-semibold tracking-tight">
            Mon profil
          </h2>
          <p className="text-white/55 text-[13px] md:text-[14px]">
            Gérez vos informations pour personnaliser votre expérience
            d'apprentissage.
          </p>
        </div>

        {currentError && (
          <div
            role="alert"
            className="flex items-center gap-3 py-3 px-5 rounded-[15px] border border-borderColor bg-[#FFE9E9] w-full sm:w-fit"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <g id="Page-1">
                <g id="Icon-Set-Filled">
                  <path
                    id="cross-circle"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.5356 12.65C13.78 12.8937 13.78 13.2938 13.5356 13.5375C13.2919 13.7813 12.8956 13.7813 12.6513 13.5375L10.0037 10.8875L7.33749 13.5562C7.09124 13.8 6.69252 13.8 6.44627 13.5562C6.20064 13.3062 6.20064 12.9063 6.44627 12.6625L9.11251 9.99374L6.465 7.35001C6.22062 7.10626 6.22062 6.70623 6.465 6.46248C6.70812 6.21873 7.10437 6.21873 7.34875 6.46248L9.99626 9.11247L12.6825 6.42502C12.9287 6.18127 13.3269 6.18127 13.5731 6.42502C13.8187 6.67502 13.8187 7.06873 13.5731 7.31873L10.8875 10.0063L13.5356 12.65ZM10 0C4.47688 0 0 4.475 0 10C0 15.525 4.47688 20 10 20C15.5231 20 20 15.525 20 10C20 4.475 15.5231 0 10 0Z"
                    fill="#CE0500"
                  />
                </g>
              </g>
            </svg>
            <span className="text-[13px] md:text-[14px]">{currentError}</span>
          </div>
        )}

        <form
          onSubmit={handleSubmit((data) => {
            if (handleValidation()) {
              onSubmit(data);
            } else {
              setActiveTab("personal");
            }
          })}
          className="w-full overflow-hidden rounded-[24px] border border-[#ffffff1a] bg-white/[0.03] backdrop-blur-sm shadow-2xl shadow-black/20"
        >
          {/* ---------- Tabs ---------- */}
          <div className="p-3 md:p-4 border-b border-[#ffffff1a]">
            <div
              role="tablist"
              aria-label="Sections du profil"
              className="relative grid grid-cols-2 p-1 rounded-[16px] bg-white/[0.04] border border-[#ffffff1a]"
            >
              {/* sliding indicator */}
              <span
                aria-hidden="true"
                className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-[12px] bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] shadow-lg shadow-[#113BE1]/30 transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)]"
                style={{ transform: `translateX(${activeIndex * 100}%)` }}
              />
              {TABS.map(({ id, label, short, icon: Icon }) => {
                const selected = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    id={`tab-${id}`}
                    aria-selected={selected}
                    aria-controls={`panel-${id}`}
                    onClick={() => setActiveTab(id)}
                    className={`relative z-10 flex items-center justify-center gap-2 h-11 px-3 rounded-[12px] text-[12px] md:text-[14px] font-medium transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${selected ? "text-white" : "text-white/55 hover:text-white/85"
                      }`}
                  >
                    <Icon className="text-base shrink-0" />
                    <span className="hidden sm:inline truncate">{label}</span>
                    <span className="sm:hidden truncate">{short}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-5 md:p-8">
            {/* ---------- Personal panel ---------- */}
            <div
              role="tabpanel"
              id="panel-personal"
              aria-labelledby="tab-personal"
              hidden={activeTab !== "personal"}
              className={activeTab === "personal" ? "pm-panel-in flex flex-col gap-8" : ""}
            >
              {/* Avatar */}
              <div className="flex flex-col sm:flex-row items-center gap-5 pb-8 border-b border-[#ffffff1a]">
                <div className="relative shrink-0 w-[112px] h-[112px]">
                  <img
                    src={user?.photoURL || ProfileImage}
                    alt="Photo de profil"
                    className="w-full h-full object-cover rounded-full ring-4 ring-[#4268FF]/30"
                  />
                  <label
                    htmlFor="profile-img"
                    className="absolute bottom-0 right-0 cursor-pointer w-[34px] h-[34px] flex items-center justify-center bg-white rounded-full shadow-lg shadow-black/30 transition-transform duration-200 hover:scale-110"
                    title="Changer la photo"
                  >
                    <GoPencil className="text-[#0075FF]" />
                  </label>
                  <input
                    id="profile-img"
                    type="file"
                    accept="image/*"
                    onChange={(e) => uploadProfileImage(e.target.files[0])}
                    className="hidden"
                  />
                </div>

                <div className="flex flex-col items-center sm:items-start gap-1 min-w-0 w-full">
                  <span className="text-white text-[16px] font-medium truncate max-w-full">
                    {user?.displayName || "Photo de profil"}
                  </span>
                  <span className="text-white/50 text-[13px] truncate max-w-full">
                    {user?.email}
                  </span>
                  {isLoading ? (
                    <div className="w-full max-w-[260px] mt-2 flex items-center gap-3">
                      <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] transition-[width] duration-200"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-white/50 text-[12px] tabular-nums">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  ) : (
                    <label
                      htmlFor="profile-img"
                      className="mt-2 inline-flex cursor-pointer items-center gap-2 h-9 px-4 rounded-[12px] border border-[#ffffff1a] text-white/80 text-[13px] transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
                    >
                      <GoPencil className="text-[#4268FF]" />
                      Changer la photo
                    </label>
                  )}
                </div>
              </div>

              {/* Personal fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                <Field label="Nom et prénom" htmlFor="name" icon={FiUser} hasError={errors.name}>
                  <input
                    type="text"
                    id="name"
                    placeholder="Nom et prénom"
                    {...register("name")}
                    className={inputClass}
                  />
                </Field>

                <Field label="Adresse mail" htmlFor="email" icon={FiMail} hasError={errors.email}>
                  <input
                    type="email"
                    id="email"
                    {...register("email")}
                    placeholder="email@domain.com"
                    className={inputClass}
                  />
                </Field>

                <Field label="Numéro de téléphone" htmlFor="phoneNumber" icon={FiPhone} hasError={errors.phoneNumber}>
                  <input
                    id="phoneNumber"
                    {...register("phoneNumber")}
                    type="number"
                    placeholder="Numéro de téléphone"
                    className={`${inputClass} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                  />
                </Field>

                <Field label="Date de naissance" htmlFor="date" hasError={errors.date}>
                  <input
                    id="date"
                    {...register("date")}
                    type="date"
                    className={`${inputClass} relative !pl-12 text-white appearance-none [color-scheme:bright]
    [&::-webkit-calendar-picker-indicator]:absolute
    [&::-webkit-calendar-picker-indicator]:left-4
    [&::-webkit-calendar-picker-indicator]:top-1/2
    [&::-webkit-calendar-picker-indicator]:-translate-y-1/2
    [&::-webkit-calendar-picker-indicator]:cursor-pointer
    [&::-webkit-calendar-picker-indicator]:opacity-40
    focus:[&::-webkit-calendar-picker-indicator]:opacity-100`}
                  />
                </Field>

                <div className="md:col-span-2">
                  <Field label="Pays de résidence" htmlFor="residence" icon={FiGlobe}>
                    <select
                      id="residence"
                      {...register("residence")}
                      className={`${inputClass} appearance-none cursor-pointer`}
                    >
                      <option value="" className="bg-blue-950 text-gray-400">
                        Sélectionnez un pays
                      </option>
                      {countries.map((country) => (
                        <option
                          key={country}
                          value={country}
                          className="bg-blue-950 text-white"
                        >
                          {country}
                        </option>
                      ))}
                    </select>
                    <svg
                      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/50"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path
                        d="M2 4l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Field>
                </div>
              </div>
            </div>

            {/* ---------- Professional panel ---------- */}
            <div
              role="tabpanel"
              id="panel-pro"
              aria-labelledby="tab-pro"
              hidden={activeTab !== "pro"}
              className={activeTab === "pro" ? "pm-panel-in flex flex-col gap-6" : ""}
            >
              <p className="text-white/50 text-[13px]">
                Ces informations nous aident à adapter vos parcours de formation
                à votre activité.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                <Field label="Pseudo" htmlFor="pseudo" icon={FiAtSign} hasError={errors.pseudo}>
                  <input
                    id="pseudo"
                    {...register("pseudo")}
                    type="text"
                    placeholder="Pseudo"
                    className={inputClass}
                  />
                </Field>

                <Field label="Fonction professionnelle" htmlFor="fonction" icon={FiBriefcase} hasError={errors.fonction}>
                  <input
                    id="fonction"
                    {...register("fonction")}
                    type="text"
                    placeholder="Fonction professionnelle"
                    className={inputClass}
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* ---------- Footer / submit ---------- */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 px-5 md:px-8 py-5 border-t border-[#ffffff1a] bg-white/[0.02]">
            <p className="text-white/40 text-[12px] text-center sm:text-start">
              Vos modifications seront appliquées à l'ensemble de votre compte.
            </p>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto min-w-[220px] h-12 py-4 px-6 flex justify-center items-center gap-2.5 rounded-[15px] bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] text-white text-[13px] md:text-[0.890rem] font-medium shadow-lg shadow-[#113BE1]/30 transition-all duration-200 hover:brightness-110 hover:shadow-[#113BE1]/50 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#4268FF]/40 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement...
                </>
              ) : (
                "Confirmer les changements"
              )}
            </button>
          </div>
        </form>

        {/* ---------- Password modal ---------- */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div
              role="dialog"
              aria-modal="true"
              className="pm-panel-in bg-white p-6 rounded-[20px] max-w-sm w-full shadow-2xl"
            >
              <div className="flex items-center justify-center w-11 h-11 rounded-full bg-[#0075FF]/10 text-[#0075FF] mb-4">
                <FiLock className="text-lg" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Confirmer le changement d'email
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                Veuillez entrer votre mot de passe actuel pour confirmer le
                changement d'email.
              </p>
              <input
                type="password"
                placeholder="Mot de passe actuel"
                className="w-full p-3 border border-gray-200 rounded-[12px] mb-5 outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    reauthenticateAndUpdateEmail(
                      pendingEmailUpdate,
                      e.target.value
                    );
                  }
                }}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 p-2.5 border border-gray-200 text-gray-700 rounded-[12px] transition-colors hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  disabled={isSubmitting}
                  onClick={(e) => {
                    const password =
                      e.target.parentElement.previousElementSibling.value;
                    reauthenticateAndUpdateEmail(pendingEmailUpdate, password);
                  }}
                  className="flex-1 p-2.5 flex items-center justify-center gap-2 bg-blue-500 text-white rounded-[12px] transition-colors hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Confirmer"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}